import { SETTINGS_BACKGROUND_KEY, SETTINGS_INDENT_KEY, SETTINGS_POSITIONING_KEY, SETTINGS_SCHEME_KEY, SETTINGS_SIDEBAR_KEY, SETTINGS_THEME_KEY, SETTINGS_VERBOSE_KEY } from "../lib/constants.js";
import { enumerateGroups } from "../lib/utils.js";
import { cachedRef, computedModel } from "../lib/utils_vue.js";
import _sfc_main$1 from "./option_bar/option_bar.vue.js";
import _sfc_main$2 from "./renderer/renderer.vue.js";
import { getComponentInfo } from "../lib/info.js";
import settings_default from "../settings.json.js";
/* empty css                                                */
import { computed, createBlock, createCommentVNode, createElementBlock, createElementVNode, createVNode, isRef, normalizeClass, onErrorCaptured, openBlock, reactive, ref, unref } from "vue";
//#region src/components/combinator.vue
var _hoisted_1 = { class: "dialtone-playground__start" };
var _sfc_main = /* @__PURE__ */ Object.assign({ name: "DtcCombinator" }, {
	props: {
		component: {
			type: Object,
			required: true
		},
		documentation: {
			type: Object,
			required: true
		},
		library: {
			type: Object,
			default: () => {}
		},
		variants: {
			type: Object,
			default: () => {}
		},
		blueprint: {
			type: Boolean,
			default: false
		},
		rootClass: {
			type: String,
			default: ""
		},
		headerClass: {
			type: String,
			default: ""
		}
	},
	setup(__props) {
		const props = __props;
		const selectedVariant = ref("default");
		const isFullScreen = ref(false);
		const codePanel = ref();
		/**
		* Container for all extended component information for the target component.
		* Immutable.
		*
		* @type {ComputedRef<object>}
		*/
		const info = computed(() => {
			return Object.freeze({
				...initializeInfo(),
				members: { enumerate(handler) {
					enumerateGroups(handler, {
						slots: info.value.slots,
						props: info.value.props,
						attributes: info.value.attributes,
						events: info.value.events
					});
				} },
				bindings: {
					get() {
						const bindings = [];
						this.enumerate((_, binding) => bindings.push(binding));
						return bindings;
					},
					enumerate(handler) {
						enumerateGroups(handler, {
							props: info.value.props,
							attributes: info.value.attributes?.filter((attribute) => attribute)
						});
					}
				}
			});
		});
		/**
		* The options data object is the main reactive object that allows interactivity with the target component.
		* Reactive.
		*
		* @type {WritableComputedRef<object>}
		*/
		const options = computedModel(
			computed(() => {
				return reactive({
					...getInitialValues(info.value),
					bindings: {
						get() {
							const bindings = [];
							this.enumerate((_, binding) => bindings.push(binding));
							return Object.fromEntries(bindings);
						},
						enumerate(handler) {
							enumerateGroups(handler, {
								props: options.value.props ? Object.entries(options.value.props) : null,
								attributes: options.value.attributes ? Object.entries(options.value.attributes) : null
							});
						}
					}
				});
			}),
			/**
			* Catch errors when updating member values,
			* else vue can block the value from ever being changed,
			* keeping the value permanently in an invalid state
			*
			* @param e
			* @param model
			*/
			(e, model) => {
				try {
					e(model);
				} catch (exception) {
					console.warn("Update options warning: \n", exception);
				}
			}
		);
		/**
		* The settings data object used to read and write cached settings used throughout the combinator.
		*
		* @type {WritableComputedRef<object>}
		*/
		const settings = computedModel(computed(() => {
			return reactive({
				root: {
					theme: cachedRef(SETTINGS_THEME_KEY, settings_default.root["default-theme"]),
					sidebar: cachedRef(SETTINGS_SIDEBAR_KEY, settings_default.root["default-sidebar"])
				},
				renderer: {
					positioning: cachedRef(SETTINGS_POSITIONING_KEY, settings_default.renderer["default-positioning"]),
					background: cachedRef(SETTINGS_BACKGROUND_KEY, settings_default.renderer["default-background"])
				},
				code: {
					scheme: cachedRef(SETTINGS_SCHEME_KEY, settings_default.code["default-scheme"]),
					indent: cachedRef(SETTINGS_INDENT_KEY, settings_default.code["default-indent-spaces"]),
					verbose: props.blueprint ? false : cachedRef(SETTINGS_VERBOSE_KEY, settings_default.code["default-verbose"])
				}
			});
		}), (e, model) => {
			try {
				e(model);
			} catch (exception) {
				console.warn("Update settings warning: \n", exception);
			}
		});
		/**
		* Gets a new instantiation of an info object.
		* Merges info from selected variant to the info object.
		*
		* @returns {object} The newly instantiated info object.
		*/
		function initializeInfo() {
			const info = getComponentInfo(props.component, props.documentation);
			const variantInfo = props.variants?.[selectedVariant.value];
			if (variantInfo) Object.entries(variantInfo).forEach(([memberGroup, members]) => {
				Object.entries(members).forEach(([memberName, member]) => {
					const infoMember = info[memberGroup].find((infoMember) => infoMember.name === memberName);
					if (infoMember) Object.assign(infoMember, member);
				});
			});
			return info;
		}
		/**
		* Gets the values for a given 'options' member group with the provided defaults.
		*
		* @param info
		*/
		function getInitialValues(info) {
			const options = {};
			info.members.enumerate((memberGroup, member) => {
				options[memberGroup] = options[memberGroup] || {};
				options[memberGroup][member.name] = member.initialValue;
			});
			return options;
		}
		function triggerEvent(event, value) {
			codePanel.value.trigger(event, value);
		}
		function toggleFullScreen($event) {
			if ($event) document.body.classList.add("d-of-hidden", "d-h100vh");
			else document.body.classList.remove("d-of-hidden", "d-h100vh");
			isFullScreen.value = $event;
		}
		onErrorCaptured((exception) => {
			console.error("Internal vue error: \n", exception);
			return false;
		});
		return (_ctx, _cache) => {
			return openBlock(), createElementBlock("div", { class: normalizeClass(["dialtone-playground", { "dialtone-playground--fullscreen": isFullScreen.value }]) }, [createElementVNode("div", _hoisted_1, [createVNode(_sfc_main$2, {
				settings: unref(settings),
				"onUpdate:settings": _cache[0] || (_cache[0] = ($event) => isRef(settings) ? settings.value = $event : null),
				class: "dialtone-playground__component",
				component: __props.component,
				info: info.value,
				options: unref(options),
				library: __props.library,
				onEvent: _cache[1] || (_cache[1] = (event, value) => triggerEvent(event, value))
			}, null, 8, [
				"settings",
				"component",
				"info",
				"options",
				"library"
			]), !__props.blueprint ? (openBlock(), createBlock(_sfc_main$1, {
				key: 0,
				options: unref(options),
				"onUpdate:options": _cache[2] || (_cache[2] = ($event) => isRef(options) ? options.value = $event : null),
				component: __props.component,
				info: info.value,
				onToggleFullScreen: toggleFullScreen
			}, null, 8, [
				"options",
				"component",
				"info"
			])) : createCommentVNode("", true)]), _cache[3] || (_cache[3] = createElementVNode("div", { class: "dialtone-playground__end" }, null, -1))], 2);
		};
	}
});
//#endregion
export { _sfc_main as default };

//# sourceMappingURL=combinator.vue.js.map